import type { Row } from '@tanstack/react-table'
import { useState } from 'react'

import {
  DataTable,
  type DataTableColumns,
  FigureCard,
  SectionHeading,
} from '@/components/ui'
import type { TableData } from '@/components/ui/table/DataTable/types'
import type { Check } from '@/modules/projects'
import type { CheckInsights } from '@/modules/projects/stores/types'
import { cn, formatNumber, numberToCurrency } from '@/utils'
import { Section } from '../shared'
import { CheckGapDetailModal } from './CheckGapDetailModal'
import {
  checkBriefColumns,
  payeeColumns,
  type SequenceGapRow,
  sequenceGapColumns,
  timelineColumns,
} from './columns'

interface ChecksInsightsProps {
  checkInsights: CheckInsights
  checks: Check[]
}

interface SummaryTile {
  label: string
  value: string
  tone?: string
}

export function ChecksInsights({ checkInsights, checks }: ChecksInsightsProps) {
  const { summary, payeeConcentration, sequenceGaps, amountFlags, timeline } =
    checkInsights
  const [selectedAccount, setSelectedAccount] = useState<string | null>(null)

  const selectedGap = selectedAccount
    ? (sequenceGaps.find((gap) => gap.accountNumber === selectedAccount) ??
      null)
    : null
  const selectedChecks = selectedAccount
    ? checks.filter((check) => check.accountNumber === selectedAccount)
    : []

  const tiles: SummaryTile[] = [
    { label: 'Checks', value: formatNumber(summary.totalChecks) },
    {
      label: 'Outflow',
      value: numberToCurrency(summary.totalOutflow),
      tone: 'text-red-600',
    },
    {
      label: 'Inflow',
      value: numberToCurrency(summary.totalInflow),
      tone: 'text-emerald-600',
    },
    { label: 'Accounts', value: formatNumber(summary.distinctAccounts) },
    { label: 'Payees', value: formatNumber(summary.distinctPayees) },
  ]

  const payeeRows = payeeConcentration.map((row) => ({ ...row, id: row.payee }))
  const gapRows = sequenceGaps.map((gap) => ({ ...gap, id: gap.accountNumber }))
  const timelineRows = timeline.map((row) => ({
    ...row,
    id: `${row.year}-${row.month}`,
  }))
  const totalMissing = sequenceGaps.reduce(
    (sum, gap) => sum + gap.missingCount,
    0,
  )

  return (
    <div className="space-y-6">
      <SectionHeading
        title="Checks"
        description="Checks are analyzed on their own, separate from the transaction ledger (a cleared check already counts there). These views surface who checks were written to, gaps in the issued check-number sequence, amount flags, and volume over time."
      />

      <div className="grid grid-cols-2 gap-3 md:grid-cols-5">
        {tiles.map((tile) => (
          <FigureCard
            key={tile.label}
            label={tile.label}
            value={tile.value}
            valueClassName={cn('text-lg', tile.tone)}
          />
        ))}
      </div>

      <Section
        title="Payee concentration"
        description="Where the money goes. Checks grouped by the payee parsed from each check's description, ranked by total outflow."
      >
        <DataTable
          columns={payeeColumns as DataTableColumns}
          data={payeeRows}
          height="h-auto"
          enableRowSelection={false}
          emptyMessage="No checks with an identified payee."
          initialPageSize={25}
          pageSizes={[25, 50, 100]}
        />
      </Section>

      <Section
        title="Sequence gaps"
        description="Missing check numbers within each account's issued range, unaccounted checks worth chasing down. Click a row to see that account's checks and missing numbers."
        count={totalMissing}
      >
        <DataTable
          columns={sequenceGapColumns as DataTableColumns}
          data={gapRows}
          height="h-auto"
          enableRowSelection={false}
          onRowClick={(row: Row<TableData>) => {
            const gapRow = row.original as unknown as SequenceGapRow
            setSelectedAccount(gapRow.accountNumber)
          }}
          emptyMessage="No gaps in any account's check-number sequence."
          initialPageSize={25}
          pageSizes={[25, 50, 100]}
        />
      </Section>

      <Section
        title="Largest checks"
        description="The biggest checks by amount, regardless of direction."
      >
        <DataTable
          columns={checkBriefColumns as DataTableColumns}
          data={amountFlags.largest}
          height="h-auto"
          enableRowSelection={false}
          emptyMessage="No checks found."
        />
      </Section>

      <Section
        title="Round-number checks"
        description="Checks for suspiciously clean amounts (exact multiples of $100), often estimates, manual entries, or transfers."
        count={amountFlags.roundNumbers.length}
      >
        <DataTable
          columns={checkBriefColumns as DataTableColumns}
          data={amountFlags.roundNumbers}
          height="h-auto"
          enableRowSelection={false}
          emptyMessage="No round-number checks found."
          initialPageSize={25}
          pageSizes={[25, 50, 100]}
        />
      </Section>

      <Section
        title="Timeline"
        description="Check volume and value per month, useful for spotting bursts of activity."
      >
        <DataTable
          columns={timelineColumns as DataTableColumns}
          data={timelineRows}
          height="h-auto"
          enableRowSelection={false}
          emptyMessage="No dated checks found."
          initialPageSize={25}
          pageSizes={[25, 50, 100]}
        />
      </Section>

      <CheckGapDetailModal
        gap={selectedGap}
        checks={selectedChecks}
        onOpenChange={(open) => {
          if (!open) setSelectedAccount(null)
        }}
      />
    </div>
  )
}
